import { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Calculator as CalcIcon, Car, Fuel, Leaf, TrendingDown } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Slider } from "./ui/slider";

const Calculator = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: "-100px" });

  const [monthlyKm, setMonthlyKm] = useState(1500);
  const [petrolPrice, setPetrolPrice] = useState(105);
  const [petrolMileage, setPetrolMileage] = useState(15);
  const [evEfficiency, setEvEfficiency] = useState(7);
  const [calculated, setCalculated] = useState(false);

  // Calculations
  const petrolCost = (monthlyKm / petrolMileage) * petrolPrice;
  const evCost = (monthlyKm / evEfficiency) * 14; // Avg Rs 14 per kWh
  const monthlySavings = petrolCost - evCost;
  const yearlySavings = monthlySavings * 12;
  const co2Saved = (monthlyKm * 0.12 * 12).toFixed(0); // Approx 120g CO2 per km for petrol

  const handleCalculate = () => {
    setCalculated(true);
  };

  return (
    <section
      className="py-24 md:py-32 bg-background-paper relative overflow-hidden"
      data-testid="calculator-section"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-[200px]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10" ref={containerRef}>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-primary font-medium text-sm uppercase tracking-wider">
            EV Calculator
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-foreground mt-4">
            CALCULATE YOUR
            <br />
            <span className="text-primary">EV SAVINGS</span>
          </h2>
          <p className="text-foreground-muted text-lg mt-6 max-w-2xl mx-auto">
            See how much you can save by switching to electric. Compare your
            current petrol costs with EV charging costs.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Calculator Inputs */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="glass-card"
          >
            <h3 className="font-heading text-xl text-foreground mb-8 flex items-center gap-3">
              <CalcIcon className="w-6 h-6 text-primary" />
              Enter Your Details
            </h3>

            <div className="space-y-8">
              {/* Monthly KM */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-foreground-muted text-sm">
                    Monthly Distance
                  </label>
                  <span className="text-primary font-heading">{monthlyKm} km</span>
                </div>
                <Slider
                  value={[monthlyKm]}
                  onValueChange={(value) => setMonthlyKm(value[0])}
                  max={5000}
                  min={100}
                  step={100}
                  className="w-full"
                  data-testid="monthly-km-slider"
                />
                <div className="flex justify-between mt-1 text-xs text-foreground-muted">
                  <span>100 km</span>
                  <span>5000 km</span>
                </div>
              </div>

              {/* Petrol Price */}
              <div>
                <label className="text-foreground-muted text-sm block mb-3">
                  Current Petrol Price (Rs/L)
                </label>
                <Input
                  type="number"
                  value={petrolPrice}
                  onChange={(e) => setPetrolPrice(Number(e.target.value))}
                  className="bg-background-subtle border-white/10 focus:border-primary text-foreground h-12"
                  data-testid="petrol-price-input"
                />
              </div>

              {/* Petrol Mileage */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-foreground-muted text-sm">
                    Your Car's Mileage (km/L)
                  </label>
                  <span className="text-secondary font-heading">{petrolMileage} km/L</span>
                </div>
                <Slider
                  value={[petrolMileage]}
                  onValueChange={(value) => setPetrolMileage(value[0])}
                  max={25}
                  min={8}
                  step={1}
                  className="w-full"
                  data-testid="petrol-mileage-slider"
                />
              </div>

              {/* EV Efficiency */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-foreground-muted text-sm">
                    EV Efficiency (km/kWh)
                  </label>
                  <span className="text-primary font-heading">{evEfficiency} km/kWh</span>
                </div>
                <Slider
                  value={[evEfficiency]}
                  onValueChange={(value) => setEvEfficiency(value[0])}
                  max={12}
                  min={4}
                  step={0.5}
                  className="w-full"
                  data-testid="ev-efficiency-slider"
                />
                <p className="text-xs text-foreground-muted mt-2">
                  * Average EV efficiency in Indian conditions
                </p>
              </div>

              <Button
                onClick={handleCalculate}
                className="w-full rounded-full bg-primary text-black font-bold hover:shadow-[0_0_20px_rgba(204,255,0,0.5)] transition-all h-14"
                data-testid="calculate-btn"
              >
                Calculate My Savings
              </Button>
            </div>
          </motion.div>

          {/* Results */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="space-y-6"
          >
            {/* Comparison Cards */}
            <div className="grid grid-cols-2 gap-4">
              <div className="glass-card text-center">
                <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                  <Fuel className="w-6 h-6 text-accent" />
                </div>
                <p className="text-foreground-muted text-sm">Petrol Cost/Month</p>
                <p className="font-heading text-3xl text-accent mt-2">
                  Rs {Math.round(petrolCost).toLocaleString()}
                </p>
              </div>
              <div className="glass-card text-center">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Car className="w-6 h-6 text-primary" />
                </div>
                <p className="text-foreground-muted text-sm">EV Cost/Month</p>
                <p className="font-heading text-3xl text-primary mt-2">
                  Rs {Math.round(evCost).toLocaleString()}
                </p>
              </div>
            </div>

            {/* Savings Card */}
            <motion.div
              initial={{ scale: 0.95 }}
              animate={calculated ? { scale: 1 } : {}}
              transition={{ type: "spring", stiffness: 300 }}
              className={`glass-card border-2 ${
                calculated ? "border-primary animate-pulse-glow" : "border-white/10"
              }`}
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center">
                  <TrendingDown className="w-8 h-8 text-black" />
                </div>
                <div>
                  <p className="text-foreground-muted text-sm">Your Monthly Savings</p>
                  <p className="font-heading text-4xl text-primary">
                    Rs {Math.round(monthlySavings).toLocaleString()}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-6 border-t border-white/10">
                <div>
                  <p className="text-foreground-muted text-sm">Yearly Savings</p>
                  <p className="font-heading text-2xl text-secondary mt-1">
                    Rs {Math.round(yearlySavings).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-foreground-muted text-sm">Cost per KM</p>
                  <p className="font-heading text-2xl text-foreground mt-1">
                    Rs {(evCost / monthlyKm).toFixed(1)}
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Environmental Impact */}
            <div className="glass-card">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-green-500/10 flex items-center justify-center">
                  <Leaf className="w-7 h-7 text-green-500" />
                </div>
                <div>
                  <p className="text-foreground-muted text-sm">Annual CO2 Reduction</p>
                  <p className="font-heading text-2xl text-green-500">
                    {co2Saved} kg
                  </p>
                  <p className="text-foreground-muted text-xs mt-1">
                    Equivalent to planting {Math.round(Number(co2Saved) / 20)} trees
                  </p>
                </div>
              </div>
            </div>

            {/* CTA */}
            <p className="text-center text-foreground-muted text-sm">
              Ready to start saving?{" "}
              <a href="#stations" className="text-primary hover:underline">
                Find a station
              </a>{" "}
              and charge today!
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Calculator;
